import {
  defineProperty,
  getOwnPropertyDescriptor,
  isPrivateKey,
  keys
} from '@rehaje/shared'

import { Iterator } from '../constants'
import { dispatch } from '../dispatch'
import { ObserveDeleteEvent, ObserveSetEvent } from '../event'
import { DEEP, REACTIVE } from '../flags'
import { isIteratorSymbols } from '../lambdas'
import { addParent, removeParent } from '../operations/parent'
import { batch, bind, trigger } from '../operations/register'
import { createProxy } from '../proxy'
import { undispatchDepth } from '../undispatch'

import type { Reactive } from '../types'
import type { TProxyHandler } from '@rehaje/shared'

export const objectHandlers: TProxyHandler<Reactive> = {
  defineProperty(target, key: string, descriptor) {
    if (isPrivateKey(key)) defineProperty(target, key, descriptor)
    else if (descriptor.get !== undefined) {
      batch(() => {
        defineProperty(target, key, descriptor)
        trigger(target, key)
        trigger(target, Iterator)
      })
    } else if (descriptor.value !== target[key]) {
      batch(() => {
        let event
        let value = descriptor.value
        if (undispatchDepth === 0) {
          event = dispatch(new ObserveSetEvent(target, key, value))
          key = event.source.key
          value = event.source.value
        }
        const oldVal = target[key]
        if (value === oldVal) return
        descriptor.value = value
        defineProperty(target, key, descriptor)
        trigger(target, key)
        trigger(target, Iterator)
        if (undispatchDepth === 0) {
          event?.__r_runEffects()
          addParent(target, key, value)
          removeParent(target, key, oldVal)
        }
      })
    }
    return true
  },
  deleteProperty(target, key: string) {
    if (isPrivateKey(key)) delete target[key]
    else if (key in target) {
      batch(() => {
        let event
        if (undispatchDepth === 0) {
          event = dispatch(new ObserveDeleteEvent(target, key))
          key = event.source.key
          if (!(key in target)) return
        }
        const oldVal = target[key]
        delete target[key]
        trigger(target, key)
        trigger(target, Iterator)
        if (undispatchDepth === 0) {
          event?.__r_runEffects()
          removeParent(target, key, oldVal)
        }
      })
    } else delete target[key]
    return true
  },
  get(target, key: string) {
    if (isPrivateKey(key)) return target[key]
    bind(target, isIteratorSymbols(key) ? Iterator : key)
    const val = target[key]
    const newVal = target.__rr_flags & DEEP ? createProxy(val) : val
    if (undispatchDepth === 0 && newVal?.__rr_flags & REACTIVE)
      addParent(target, key, newVal)
    return newVal
  },
  getOwnPropertyDescriptor(target, key) {
    if (isPrivateKey(key)) return
    return getOwnPropertyDescriptor(target, key)
  },
  has(target, key: string) {
    if (isPrivateKey(key)) return key in target
    bind(target, key)
    return key in target
  },
  ownKeys(target) {
    bind(target, Iterator)
    return keys(target)
  },
  set(target, key: string, value) {
    if (isPrivateKey(key)) target[key] = value
    else if (value !== target[key]) {
      batch(() => {
        let event
        if (undispatchDepth === 0) {
          event = dispatch(new ObserveSetEvent(target, key, value))
          key = event.source.key
          value = event.source.value
        }
        const oldVal = target[key]
        if (value === oldVal) return
        target[key] = value
        trigger(target, key)
        trigger(target, Iterator)
        if (undispatchDepth === 0) {
          event?.__r_runEffects()
          addParent(target, key, value)
          removeParent(target, key, oldVal)
        }
      })
    }
    return true
  }
}
